import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, ShieldAlert } from "lucide-react";
import { Spinner } from "@/components/ui/Spinner";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useCareOverview } from "../hooks";
import {
  PageHeader,
  TableShell,
  Th,
} from "../components/ui";
import type { CareOverviewRow } from "../types";

/**
 * Rejestr „szczególnych oddziałów" — WSZYSTKO o kartach specyfiki w jednym miejscu
 * (moduł „trudnego klienta"). Jeden wiersz = jeden poziom (klient / oddział /
 * pododdział), który ma choć jedną flagę, ustalenie albo obserwację.
 *
 * Kartę prowadzi się w projekcie (zakładka „Opieka"), więc rejestr jest widokiem
 * przeglądowym: pokazuje, gdzie są sygnały i ile, oraz prowadzi wprost do projektów
 * danego poziomu. Dane wewnętrzne — strona żyje za bramką modułu DLC_01.
 */

const SCOPE_LABEL: Record<CareOverviewRow["scope"], string> = {
  client: "Klient",
  branch: "Oddział",
  sub_branch: "Pododdział",
};

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("pl-PL");

/** Licznik w komórce: liczba + podpis; zero jest wyszarzone, nie znika. */
function Count({ n, label, tone }: { n: number; label: string; tone?: "amber" }) {
  return (
    <span
      className="inline-flex items-baseline gap-1"
      title={label}
      style={{ color: n > 0 && tone === "amber" ? "#b45309" : undefined }}
    >
      <span
        className={`font-mono text-sm font-bold tabular-nums ${
          n > 0 ? "" : "text-[var(--color-text-muted)]"
        }`}
      >
        {n}
      </span>
      <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)]">
        {label}
      </span>
    </span>
  );
}

function Row({ row }: { row: CareOverviewRow }) {
  const [open, setOpen] = useState(false);
  // Do karty wchodzi się przez dowolny projekt poziomu — bierzemy pierwszy.
  const entryProject = row.projects[0];

  return (
    <>
      <tr className="border-t border-[var(--color-border-muted)] hover:bg-[var(--color-bg-elevated)] transition-colors">
        <td className="px-3 py-2.5 w-8">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
            aria-label={open ? "Zwiń szczegóły" : "Rozwiń szczegóły"}
          >
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </button>
        </td>
        <td className="px-3 py-2.5">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] border border-[var(--color-border-strong)] px-1">
              {SCOPE_LABEL[row.scope]}
            </span>
            <span className="font-medium text-[var(--color-text-primary)]">{row.name}</span>
          </div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">
            {row.path.join("  →  ")}
          </div>
        </td>
        <td className="px-3 py-2.5">
          {row.flags.length > 0 ? (
            <div className="flex flex-wrap gap-1">
              {row.flags.map((f) => (
                <span
                  key={f.id}
                  className="inline-flex items-center gap-0.5 border border-amber-500 bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-700 dark:text-amber-400"
                  title={f.reason || f.label}
                >
                  <ShieldAlert size={10} /> {f.label}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          )}
        </td>
        <td className="px-3 py-2.5 whitespace-nowrap">
          <div className="flex flex-col gap-0.5">
            <Count n={row.required_steps.length} label="kamieni" tone="amber" />
            <Count n={row.warnings.length} label="ostrzeżeń" />
          </div>
        </td>
        <td className="px-3 py-2.5">
          {row.last_note ? (
            <div className="max-w-[320px]">
              <p className="text-[13px] text-[var(--color-text-primary)] line-clamp-2">
                {row.last_note.text}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-wider text-[var(--color-text-muted)] mt-0.5">
                {row.last_note.author_name ?? "—"} · {fmtDate(row.last_note.created_at)}
                {row.last_note.project_name && (
                  <> · projekt: {row.last_note.project_name}</>
                )}
                {row.notes_count > 1 && <> · z {row.notes_count} wpisów</>}
              </p>
            </div>
          ) : (
            <span className="text-[var(--color-text-muted)]">—</span>
          )}
        </td>
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          <span className="font-mono text-sm tabular-nums text-[var(--color-text-secondary)]">
            {row.projects.length}
          </span>
        </td>
        <td className="px-3 py-2.5 text-right whitespace-nowrap">
          {entryProject ? (
            <Link
              to={`/power-projects/${entryProject.project_id}?tab=metadata`}
              className="border border-[var(--color-border-strong)] px-2 py-1 text-[11px] text-[var(--color-text-secondary)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors"
              title="Otwórz kartę specyfiki (zakładka „Opieka”) na projekcie tego poziomu"
            >
              Otwórz kartę
            </Link>
          ) : (
            <span
              className="font-mono text-[10px] uppercase text-[var(--color-text-muted)]"
              title="Ten poziom nie ma jeszcze aktywnych projektów ZE — kartę otworzysz z dowolnego projektu po przypięciu"
            >
              brak projektów
            </span>
          )}
        </td>
      </tr>

      {open && (
        <tr className="bg-[var(--color-bg-elevated)]">
          <td />
          <td colSpan={6} className="px-3 py-3">
            <div className="grid gap-4 md:grid-cols-3">
              <div>
                <p className="section-label mb-1.5">Wymagane kroki (kamienie)</p>
                {row.required_steps.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)]">—</p>
                ) : (
                  <ul className="space-y-1">
                    {row.required_steps.map((s) => (
                      <li key={s.id} className="text-[13px] text-[var(--color-text-primary)]">
                        • {s.label}
                        {s.text && (
                          <span className="block text-[11px] text-[var(--color-text-muted)]">
                            {s.text}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="section-label mb-1.5">Ostrzeżenia</p>
                {row.warnings.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)]">—</p>
                ) : (
                  <ul className="space-y-1">
                    {row.warnings.map((w) => (
                      <li key={w.id} className="text-[13px] text-[var(--color-text-primary)]">
                        • {w.label}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <p className="section-label mb-1.5">Projekty tego poziomu · {row.projects.length}</p>
                {row.projects.length === 0 ? (
                  <p className="text-xs text-[var(--color-text-muted)]">—</p>
                ) : (
                  <ul className="space-y-0.5 max-h-40 overflow-y-auto">
                    {row.projects.map((p) => (
                      <li key={p.project_id}>
                        <Link
                          to={`/power-projects/${p.project_id}`}
                          className="text-[13px] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:underline"
                        >
                          {p.project_name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function CareRegistryPage() {
  usePageTitle("Szczególna opieka");
  const { data = [], isLoading } = useCareOverview();
  const [search, setSearch] = useState("");
  const [scope, setScope] = useState<"" | CareOverviewRow["scope"]>("");
  const [onlySteps, setOnlySteps] = useState(false);

  const rows = useMemo(
    () =>
      data.filter((r) => {
        if (scope && r.scope !== scope) return false;
        if (onlySteps && r.required_steps.length === 0) return false;
        const q = search.trim().toLowerCase();
        if (!q) return true;
        return [
          ...r.path,
          ...r.flags.map((f) => `${f.label} ${f.reason}`),
          ...r.required_steps.map((s) => s.label),
          ...r.warnings.map((w) => w.label),
          r.last_note?.text ?? "",
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);
      }),
    [data, search, scope, onlySteps],
  );

  const totals = useMemo(
    () => ({
      levels: data.length,
      flags: data.reduce((n, r) => n + r.flags.length, 0),
      steps: data.reduce((n, r) => n + r.required_steps.length, 0),
      notes: data.reduce((n, r) => n + r.notes_count, 0),
    }),
    [data],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Szczególna opieka"
        subtitle="Rejestr klientów, oddziałów i pododdziałów z kartą specyfiki · DLC_01"
      />

      <div className="flex flex-wrap items-center gap-2">
        <input
          placeholder="Szukaj: oddział, flaga, kamień, obserwacja…"
          className="input-base w-full sm:w-auto sm:flex-1 sm:min-w-[240px] sm:max-w-md"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="input-base w-auto"
          value={scope}
          onChange={(e) => setScope(e.target.value as "" | CareOverviewRow["scope"])}
        >
          <option value="">Poziom: wszystkie</option>
          <option value="client">Klient</option>
          <option value="branch">Oddział</option>
          <option value="sub_branch">Pododdział</option>
        </select>
        <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={onlySteps}
            onChange={(e) => setOnlySteps(e.target.checked)}
          />
          tylko z wymaganym kamieniem
        </label>
        <span className="section-label ml-auto whitespace-nowrap">
          {rows.length} / {totals.levels} · {totals.flags} flag · {totals.steps} kamieni ·{" "}
          {totals.notes} wpisów
        </span>
      </div>

      {isLoading ? (
        <div className="py-10 flex justify-center">
          <Spinner />
        </div>
      ) : data.length === 0 ? (
        <div className="card border-[var(--color-border-strong)] p-6">
          <p className="text-sm text-[var(--color-text-secondary)]">
            Nic tu jeszcze nie ma. Kartę specyfiki zakłada się z projektu ZE: zakładka{" "}
            <strong>Opieka</strong> → „+ Flaga” (oznaczenie trudnego oddziału) albo
            „+ Ustalenie” → „Wymagany krok (kamień)”. Wpisy obowiązują cały poziom
            (klient / oddział / pododdział), więc pojawią się tutaj od razu.
          </p>
        </div>
      ) : (
        <TableShell>
          <thead>
            <tr>
              <Th className="w-8" />
              <Th>Poziom</Th>
              <Th>Flagi</Th>
              <Th>Ustalenia</Th>
              <Th>Ostatnia obserwacja</Th>
              <Th className="text-right">Projekty</Th>
              <Th className="w-28" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Row key={`${r.scope}:${r.id}`} row={r} />
            ))}
          </tbody>
        </TableShell>
      )}

      <p className="text-[11px] text-[var(--color-text-muted)]">
        Dane wewnętrzne GK ELEKTIM — niewidoczne dla kont podwykonawców, nie trafiają do
        generowanych dokumentów.
      </p>
    </div>
  );
}
